/* ===================================================================
   useNotificationPreferences() + update mutation -- owner new-review
   alert routing (Hopper, 2026-06-09).

   Read:   GET  /api/v1/notification-preferences  (saved row, or defaults)
   Write:  PUT  /api/v1/notification-preferences  (any subset of fields)

   Brad's Settings -> Notifications tab binds to these. The read hook is
   disabled until a Bearer rr_ token exists (same pattern as the other
   data hooks) so it never auto-fires a 401 on first paint. After a save
   we invalidate the prefs query so the tab reflects the new state.

   MODEL REMINDER (for the UI copy on that tab):
     - Email is the default channel: INSTANT for 1-2 star, positives follow
       positives_mode (instant | digest | off).
     - SMS is OPT-IN, negatives only, to the owner's own number.
     - The in-app bell is ALWAYS on and is NOT controlled here.
   =================================================================== */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  api,
  type NotificationPreferences,
  type NotificationPreferencesResponse,
} from '../lib/api';
import { useApiToken } from './useApiToken';
import { qk } from './queryKeys';

export function useNotificationPreferences(
  params: { enabled?: boolean } = {},
): UseQueryResult<NotificationPreferencesResponse, Error> {
  const hasToken = Boolean(useApiToken());
  return useQuery({
    queryKey: qk.notificationPreferences(),
    queryFn: ({ signal }) => api.getNotificationPreferences(signal),
    enabled: hasToken && params.enabled !== false,
    staleTime: 60_000,
  });
}

/* The editable subset of preference fields the Settings tab can save. */
export type NotificationPreferencesPatch = Partial<
  Pick<
    NotificationPreferences,
    | 'email_enabled'
    | 'sms_enabled'
    | 'push_enabled'
    | 'trigger_scope'
    | 'positives_mode'
    | 'digest_frequency'
    | 'owner_email'
    | 'owner_phone'
  >
>;

export interface UseUpdateNotificationPreferencesResult {
  save: (patch: NotificationPreferencesPatch) => Promise<NotificationPreferencesResponse>;
  isPending: boolean;
  error: Error | null;
}

export function useUpdateNotificationPreferences(): UseUpdateNotificationPreferencesResult {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (patch: NotificationPreferencesPatch) =>
      api.updateNotificationPreferences(patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.notificationPreferences() });
    },
  });
  return {
    save: (patch) => m.mutateAsync(patch),
    isPending: m.isPending,
    error: m.error ?? null,
  };
}
